import {
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  createTheme,
  ThemeProvider,
  Button,
  Fade,
  Chip,
  DialogContent,
  IconButton,
  Dialog,
} from "@mui/material";
import SportsCricketIcon from "@mui/icons-material/SportsCricket";
import SportsBaseballIcon from "@mui/icons-material/SportsBaseball";
import BatCageFacility from "../assets/the-bat-cage-facility.webp";
import { teamMembers } from "./about/AboutData";
import MissionImg from "../assets/mission.webp";
import VisionImg from "../assets/vision.webp";
import Slide from "@mui/material/Slide";
import { useState } from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { getVideoUrl } from "../apis/LaneCageApi";
import CloseIcon from "@mui/icons-material/Close";

// Enhanced theme with better typography and accessibility
const theme = createTheme({
  palette: {
    primary: {
      main: "#7e04de", // Vibrant Purple
      light: "#aa47ff",
      dark: "#5c01a3",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#068cc1", // Bright Blue
      light: "#4db6e6",
      dark: "#04658c",
      contrastText: "#ffffff",
    },
    background: {
      default: "#f9f9fc",
      paper: "#ffffff",
    },
    text: {
      primary: "#3c3759", // Deep Purple/Indigo
      secondary: "#0d6186", // Deep Teal
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 700,
      letterSpacing: "-0.5px",
    },
    h4: {
      fontWeight: 600,
      letterSpacing: "-0.25px",
    },
    h6: {
      fontWeight: 500,
    },
    body1: {
      fontSize: "1.05rem",
      lineHeight: 1.7,
    },
    body2: {
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "10px 24px",
          fontWeight: 500,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0px 6px 16px rgba(0, 0, 0, 0.08)",
        },
      },
    },
  },
});

// Fixed CustomIconButton component that properly accepts props
const CustomIconButton = (props: any) => {
  const { children, color, onClick } = props;

  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "none",
        bgcolor: "transparent",
        color: `${color}.main`,
        cursor: "pointer",
        transition: "all 0.2s",
        mx: 0.5,
        "&:hover": {
          bgcolor: `${color}.light`,
          color: "white",
        },
      }}
    >
      {children}
    </Box>
  );
};

const CustomTimeline = () => {
  const timelineItems = [
    {
      icon: (
        <img
          src={MissionImg}
          alt="Mission"
          style={{ width: "100%", height: "100%" }}
        />
      ),
      title: "Mission",
      description:
        "At The Bat Cage, our mission is to provide athletes of all ages and skill levels with a top-tier, indoor facility where they can train, improve, and enjoy their favorite sports. We are committed to fostering a supportive and dynamic environment that encourages growth in cricket, baseball and softball. Our goal is to inspire passion, teamwork, and excellence, helping players reach their full potential while having fun along the way.",
      alternate: false,
    },
    {
      icon: (
        <img
          src={VisionImg}
          alt="Vision"
          style={{ width: "100%", height: "100%" }}
        />
      ),
      title: "Vision",
      description:
        "At The Bat Cage, our vision is to become the leading sports facility in the region, empowering athletes of all levels to excel in cricket and baseball. We aim to foster a vibrant community of sports enthusiasts by providing top-tier facilities, cutting-edge equipment, and a supportive environment where passion and skill come together. Our goal is to inspire the next generation of athletes to achieve their full potential, both on and off the field, through dedication, innovation, and an unwavering commitment to excellence.",
      alternate: true,
    },
  ];

  return (
    <Box sx={{ py: 4 }}>
      {timelineItems.map((item, index) => (
        <Fade in={true} timeout={500 + index * 300} key={index}>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              margin: "0 auto",
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                md: item.alternate ? "row-reverse" : "row",
              },
              alignItems: { xs: "center", md: "center" },
              mb: 6,
              textAlign: { xs: "center", md: item.alternate ? "left" : "left" },
            }}
          >
            {/* Image Container */}
            <Box
              sx={{
                width: "50%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                color: "white",
                boxShadow: 3,
                mb: { xs: 2, md: 0 },
                mx: { md: 3 },
              }}
            >
              {item.icon}
            </Box>

            {/* Description Container */}
            <Box
              sx={{
                flex: 1,
                justifyContent: "space-between",
                ml: { xs: 0, md: 3 },
              }}
            >
              <Typography
                variant="h5"
                color={item.alternate ? "secondary" : "primary"}
                gutterBottom
                fontWeight="500"
              >
                {item.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {item.description}
              </Typography>
            </Box>
          </Box>
        </Fade>
      ))}
    </Box>
  );
};

const AboutUs = () => {
  const [currentCard, setCurrentCard] = useState(0);
  const [slideDirection, setSlideDirection] = useState<
    "right" | "left" | undefined
  >("left");
  const [videoUrl, setVideoUrl] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const cardsToShow = 3;
  const navigate = useNavigate();

  const handleOpenVideoModal = async () => {
    try {
      const response = await getVideoUrl();
      setVideoUrl(response.url);
      setOpenModal(true);
    } catch (error: any) {
      console.error("Error fetching video URL:", error.message);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleNextCard = () => {
    setSlideDirection("left");
    setCurrentCard((prevCard) => (prevCard + 1) % teamMembers.length);
  };

  const handlePrevCard = () => {
    setSlideDirection("right");
    setCurrentCard(
      (prevCard) => (prevCard - 1 + teamMembers.length) % teamMembers.length
    );
  };

  const visibleMembers = teamMembers.slice(
    currentCard,
    currentCard + cardsToShow
  );

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{ bgcolor: "background.default", minHeight: "100vh", pt: 2, pb: 8 }}
      >
        <Box sx={{ width: "80vw", margin: "auto" }}>
          {/* Hero Section with enhanced styling */}
          <Box
            textAlign="center"
            mb={8}
            mt={4}
            sx={{
              backgroundImage:
                "linear-gradient(135deg, rgba(126,4,222,0.05) 0%, rgba(6,140,193,0.05) 100%)",
              borderRadius: 4,
              py: 6,
              px: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            }}
          >
            <Typography variant="h3" color="primary" gutterBottom>
              About The Bat Cage
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: "auto" }}
            >
              Empowering Athletes, One Swing at a Time
            </Typography>
            <Box mt={4}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                sx={{ mr: 2, mb: { xs: 2, sm: 0 } }}
                onClick={() => navigate("/listings")}
              >
                Book a Session
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={handleOpenVideoModal}
              >
                Take a Tour
              </Button>
            </Box>
          </Box>

          {/* Our Story Section with enhanced layout */}
          <Grid container spacing={6} alignItems="center" mb={10}>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" color="primary" gutterBottom>
                Our Story
              </Typography>
              <Typography variant="body1" paragraph>
                The Bat Cage was founded with a simple goal: to create a
                dedicated and welcoming space for players of all ages and skill
                levels to train, improve, and enjoy the game. Born out of a
                passion for sport and a desire to support our local community,
                The Bat Cage offers a professional yet friendly environment
                where athletes can sharpen their skills year-round.
              </Typography>
              <Typography variant="body1" paragraph>
                From the beginning, we recognized the need for accessible,
                high-quality training facilities in the region — a place where
                players could focus on their swing, strengthen fundamentals, and
                prepare for success on and off the field. Our facility is
                equipped with modern batting cages, and professional-grade
                equipment ensuring players get the best possible training
                experience.
              </Typography>
              <Typography variant="body1" paragraph>
                At The Bat Cage, we are committed to more than just practice —
                we believe in building confidence, sportsmanship, and a strong
                community around the game we all love. Whether you're stepping
                up to the plate for the first time or preparing for competitive
                play, The Bat Cage is here to help you hit your goals, one pitch
                at a time.
              </Typography>
              <Box mt={3}>
                <Chip
                  icon={<SportsCricketIcon />}
                  label="Cricket"
                  color="primary"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  icon={<SportsBaseballIcon />}
                  label="Baseball"
                  color="secondary"
                  sx={{ mr: 1, mb: 1 }}
                />
                <Chip
                  label="Field Hockey"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                elevation={4}
                sx={{
                  overflow: "hidden",
                  borderRadius: 3,
                  transform: "perspective(1000px) rotateY(-5deg)",
                }}
              >
                <CardMedia
                  component="img"
                  height="400"
                  image={BatCageFacility}
                  alt="The Bat Cage Facility"
                  sx={{
                    transition: "transform 0.6s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                />
              </Card>
            </Grid>
          </Grid>

          {/* Our Mission Section with enhanced styling */}
          <Box
            mb={10}
            sx={{
              bgcolor: "white",
              py: 6,
              px: 4,
              borderRadius: 4,
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            }}
          >
            <Typography
              variant="h4"
              color="primary"
              textAlign="center"
              gutterBottom
              fontWeight="bold"
            >
              Our Mission
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              paragraph
              sx={{ maxWidth: 800, mx: "auto", mb: 6 }}
            >
              At The Bat Cage, we're committed to excellence in sports training
              through these core principles:
            </Typography>
            <Box>
              <CustomTimeline />
            </Box>
          </Box>

          {/* Team Section with enhanced cards */}
          <Box mb={10}>
            <Typography
              variant="h4"
              color="primary"
              textAlign="center"
              gutterBottom
              fontWeight="bold"
            >
              Meet Our Expert Coaches
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              paragraph
              sx={{ maxWidth: 800, mx: "auto", mb: 6 }}
            >
              Our passionate team of experienced coaches is dedicated to helping
              you achieve your athletic goals.
            </Typography>

            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CustomIconButton
                onClick={handlePrevCard}
                color="primary"
                sx={{ position: "absolute", left: 0, zIndex: 1 }}
              >
                <ArrowBackIosIcon />
              </CustomIconButton>

              <Grid container spacing={4} justifyContent="center">
                {visibleMembers.map((member, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Slide
                      direction={slideDirection}
                      in={true}
                      mountOnEnter
                      unmountOnExit
                    >
                      <Card
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-8px)",
                            boxShadow: "0 12px 20px rgba(0,0,0,0.15)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            height: 180,
                            overflow: "hidden",
                            background:
                              "linear-gradient(135deg, rgba(126,4,222,0.8) 0%, rgba(6,140,193,0.8) 100%)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "flex-end",
                            pb: 2,
                          }}
                        >
                          <Avatar
                            alt={member.name}
                            src={member.image}
                            sx={{
                              width: 160,
                              height: 160,
                              border: "4px solid white",
                              boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                            }}
                          />
                        </Box>
                        <CardContent
                          sx={{ textAlign: "center", pt: 3, flexGrow: 1 }}
                        >
                          <Typography variant="h6" color="primary" gutterBottom>
                            {member.name}
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            color="secondary"
                            gutterBottom
                            fontWeight="500"
                          >
                            {member.role}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {member.bio}
                          </Typography>
                          <Box mb={2}>
                            {member.specialties.map((specialty, i) => (
                              <Chip
                                key={i}
                                size="small"
                                label={specialty}
                                sx={{ m: 0.5 }}
                                color={i % 2 === 0 ? "primary" : "secondary"}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Slide>
                  </Grid>
                ))}
              </Grid>

              <CustomIconButton
                onClick={handleNextCard}
                color="primary"
                sx={{ position: "absolute", right: 0, zIndex: 1 }}
              >
                <ArrowForwardIosIcon />
              </CustomIconButton>
            </Box>
          </Box>
        </Box>
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="lg"
          fullWidth
        >
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              zIndex: 2,
              backgroundColor: "rgba(0,0,0,0.3)",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.5)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent
            sx={{ padding: 0, overflow: "hidden", bgcolor: "#000" }}
          >
            {videoUrl && (
              <video
                src={videoUrl}
                style={{ width: "100%", height: "auto" }}
                controls
                autoPlay
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default AboutUs;
